import React, { useMemo } from 'react';
import { CreditCard, Shield } from 'lucide-react';

interface BillingCardProps {
  actualCost?: number;
  estimatedCost?: number;
  maxAuthorizedCost?: number;
  fileName?: string;
  totalProcessed: number;
}

export const BillingCard: React.FC<BillingCardProps> = ({
  actualCost,
  estimatedCost,
  maxAuthorizedCost,
  fileName,
  totalProcessed,
}) => {
  const costSavings = useMemo(() => {
    if (!maxAuthorizedCost || !actualCost) {
      return 0;
    }
    return Number((maxAuthorizedCost - actualCost).toFixed(2));
  }, [maxAuthorizedCost, actualCost]);

  return (
    <div className="grid md:grid-cols-2 gap-4 mt-6">
      <div className="border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-3">
          <CreditCard className="w-5 h-5 text-slate-600" />
          <h2 className="text-base font-semibold text-slate-900">Billing Summary</h2>
        </div>
        <dl className="space-y-2 text-sm text-slate-600">
          <div className="flex justify-between">
            <dt>Actual charge</dt>
            <dd className="font-semibold text-slate-900">
              {actualCost !== undefined ? `$${actualCost.toFixed(2)}` : '—'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt>Estimated charge</dt>
            <dd className="font-medium text-slate-900">
              {estimatedCost !== undefined ? `$${estimatedCost.toFixed(2)}` : '—'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt>Original authorization</dt>
            <dd className="font-medium text-slate-900">
              {maxAuthorizedCost !== undefined ? `$${maxAuthorizedCost.toFixed(2)}` : '—'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt>Hold released</dt>
            <dd className="font-medium text-green-600">${costSavings.toFixed(2)}</dd>
          </div>
        </dl>
      </div>
      <div className="border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-3">
          <Shield className="w-5 h-5 text-slate-600" />
          <h2 className="text-base font-semibold text-slate-900">File Details</h2>
        </div>
        <dl className="space-y-2 text-sm text-slate-600">
          <div className="flex justify-between">
            <dt>File name</dt>
            <dd className="font-medium text-slate-900">{fileName ?? 'Uploaded file'}</dd>
          </div>
          <div className="flex justify-between">
            <dt>Total processed</dt>
            <dd className="font-medium text-slate-900">{totalProcessed.toLocaleString()}</dd>
          </div>
          <div className="flex justify-between">
            <dt>Results retention</dt>
            <dd className="font-medium text-slate-900">90 days</dd>
          </div>
        </dl>
      </div>
    </div>
  );
};
